import { TellescopeModelFromName } from "../../mappings";

/**
 * Determines if two threads match (represent the same conversation).
 * Based on the threads_match function from api/modules/inbox.ts
 *
 * Matching logic:
 * - Email: Same type, same enduser, and (same emailMessageId OR same threadId)
 * - SMS: Same type, same enduser, same phoneNumber, same enduserPhoneNumber
 * - Phone: Same type, same enduser, same phoneNumber, same enduserPhoneNumber
 * - Chat: Same type, same threadId (roomId)
 * - GroupMMS: Same type, same threadId (conversation id)
 */
export const threadsMatch = <T extends Partial<TellescopeModelFromName<"inbox_threads">>>(t1: T, t2: T): boolean => {
  if (t1.type !== t2.type) return false;

  switch (t1.type) {
    case "Email":
      return (
        t1.enduserIds?.[0] === t2.enduserIds?.[0] &&
        ((t1.emailMessageId && t1.emailMessageId === t2.emailMessageId) || t1.threadId === t2.threadId)
      );

    case "SMS":
    case "Phone":
      return (
        t1.enduserIds?.[0] === t2.enduserIds?.[0] &&
        t1.phoneNumber === t2.phoneNumber &&
        t1.enduserPhoneNumber === t2.enduserPhoneNumber
      );

    case "Chat":
    case "GroupMMS":
      return t1.threadId === t2.threadId;

    default:
      // Fallback: compare threadId
      return t1.threadId === t2.threadId;
  }
};

/**
 * Merges two threads, keeping the most recent information.
 * When timestamps are equal, prioritizes t2 (the newer one in processing order).
 *
 * Based on the merge logic from merge_threads in api/modules/inbox.ts
 */
const mergeThreads = <T extends Partial<TellescopeModelFromName<"inbox_threads">>>(t1: T, t2: T): T => {
  const t1Time = t1.timestamp ? new Date(t1.timestamp).getTime() : undefined;
  const t2Time = t2.timestamp ? new Date(t2.timestamp).getTime() : undefined;
  const t1OutboundTime = t1.outboundTimestamp ? new Date(t1.outboundTimestamp).getTime() : 0;
  const t2OutboundTime = t2.outboundTimestamp ? new Date(t2.outboundTimestamp).getTime() : 0;

  // Determine which thread is newer based on inbound timestamp
  const t2IsNewer = t1Time === undefined ? true : t2Time === undefined ? false : t2Time > t1Time;
  const timestampsEqual = t2Time === t1Time;

  // Check outbound timestamps for additional updates
  const t2HasNewerOutbound = t2OutboundTime > t1OutboundTime;
  const outboundTimestampsEqual = t2OutboundTime === t1OutboundTime;

  // If t2 has newer inbound OR newer outbound, use t2 as base
  if (t2IsNewer || t2HasNewerOutbound) {
    return {
      ...t2,
      // Merge draft and scheduled message IDs from both
      draftMessageIds: [...(t1.draftMessageIds || []), ...(t2.draftMessageIds || [])].filter(
        (id, index, self) => self.indexOf(id) === index
      ), // dedupe
      scheduledMessageIds: [...(t1.scheduledMessageIds || []), ...(t2.scheduledMessageIds || [])].filter(
        (id, index, self) => self.indexOf(id) === index
      ), // dedupe
    };
  }

  // If timestamps are equal, prefer t2 but merge metadata
  if (timestampsEqual || outboundTimestampsEqual) {
    return {
      ...t2,
      // If t2 doesn't have outbound info but t1 does, keep t1's outbound
      ...(!t2.outboundTimestamp && t1.outboundTimestamp
        ? {
            outboundTimestamp: t1.outboundTimestamp,
            outboundPreview: t1.outboundPreview,
            recentOutboundUserId: t1.recentOutboundUserId,
          }
        : {}),
      // Merge draft and scheduled message IDs
      draftMessageIds: [...(t1.draftMessageIds || []), ...(t2.draftMessageIds || [])].filter(
        (id, index, self) => self.indexOf(id) === index
      ),
      scheduledMessageIds: [...(t1.scheduledMessageIds || []), ...(t2.scheduledMessageIds || [])].filter(
        (id, index, self) => self.indexOf(id) === index
      ),
    };
  }

  // t1 is newer, keep it
  return {
    ...t1,
    // Merge draft and scheduled message IDs
    draftMessageIds: [...(t1.draftMessageIds || []), ...(t2.draftMessageIds || [])].filter(
      (id, index, self) => self.indexOf(id) === index
    ),
    scheduledMessageIds: [...(t1.scheduledMessageIds || []), ...(t2.scheduledMessageIds || [])].filter(
      (id, index, self) => self.indexOf(id) === index
    ),
  };
};

/**
 * De-duplicates a list of inbox threads, keeping only the latest version of each unique thread.
 *
 * This utility handles cases where multiple messages belong to the same conversation thread:
 * - Two SMS messages sent to/from the same phone numbers and enduser
 * - Multiple emails in the same email thread (same subject or messageId)
 * - Multiple updates to the same ChatRoom
 * - Multiple phone calls to/from the same numbers and enduser
 * - Multiple messages in the same GroupMMS conversation
 *
 * Deduplication logic (from api/modules/inbox.ts):
 * 1. Groups threads by matching criteria (type-specific, see threadsMatch)
 * 2. For matching threads, keeps the one with the most recent timestamp
 * 3. Merges metadata like draftMessageIds and scheduledMessageIds
 * 4. Preserves both inbound and outbound information across merges
 *
 * @param threads - Array of partial InboxThreads from conversion utilities
 * @returns Deduplicated array with only the latest version of each thread
 *
 * @example
 * const sms1 = convertSmsMessageToInboxThread(message1); // timestamp: 10:00 AM
 * const sms2 = convertSmsMessageToInboxThread(message2); // timestamp: 10:05 AM (same thread)
 * const email = convertEmailToInboxThread(emailMessage);
 *
 * const deduplicated = dedupeInboxThreads([sms1, sms2, email]);
 * // Result: [sms2, email] - only the latest SMS thread (10:05 AM) remains
 */
export const dedupeInboxThreads = <T extends Partial<TellescopeModelFromName<"inbox_threads">>>(threads: T[]): T[] => {
  if (threads.length === 0) return [];

  const deduplicated: T[] = [];

  for (const thread of threads) {
    // Find if this thread matches any existing deduplicated thread
    const existingIndex = deduplicated.findIndex((existing) => threadsMatch(thread, existing));

    if (existingIndex === -1) {
      // New unique thread, add it
      deduplicated.push(thread);
    } else {
      // Matching thread found, merge them
      const existing = deduplicated[existingIndex];
      if (existing) {
        deduplicated[existingIndex] = mergeThreads(existing, thread);
      }
    }
  }

  return deduplicated;
};

/**
 * Helper to sort threads by timestamp (newest first).
 * Useful after deduplication to present threads in order.
 */
export const sortThreadsByTimestamp = <T extends Partial<TellescopeModelFromName<"inbox_threads">>>(
  threads: T[],
  order: "newest" | "oldest" = "newest"
): T[] => {
  return [...threads].sort((a, b) => {
    const aTime = a.timestamp ? new Date(a.timestamp).getTime() : 0;
    const bTime = b.timestamp ? new Date(b.timestamp).getTime() : 0;
    return order === "newest" ? bTime - aTime : aTime - bTime;
  });
};
